/**
 * Standard Number and Currency Formatting Helpers
 * Ensures consistent display across all dashboards, components, and tables:
 * - Currency: $#,##0.00 (with commas and two decimals). Negative formatted as ($45.20).
 * - Number: #,##0.00 (or custom decimals). Negative formatted as (45.20).
 * - Percentage: 0.0% or 0.00%
 * - Hours: 0.0 hrs or 0.00 hrs
 * - Payroll Export Number: 45.20 or -45.20 (no commas or symbols for payroll system ingestion)
 */

/**
 * Formats a number as USD currency: $1,234.56 or ($1,234.56) for negative numbers
 */
export const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '$0.00';
  }

  const isNegative = amount < 0;
  const absAmount = Math.abs(amount);

  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(absAmount);

  return isNegative ? `(${formatted})` : formatted;
};

/**
 * Formats a generic number with commas and specified decimal places: 1,234.56 or (1,234.56)
 */
export const formatNumber = (value: number | null | undefined, decimals: number = 2): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return (0).toFixed(decimals);
  }

  const isNegative = value < 0;
  const absValue = Math.abs(value);

  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(absValue);

  return isNegative ? `(${formatted})` : formatted;
};

/**
 * Formats a percentage value: e.g. 12.5%
 */
export const formatPercentage = (value: number | null | undefined, decimals: number = 1): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return `0.${'0'.repeat(decimals)}%`;
  }
  const isNegative = value < 0;
  const absValue = Math.abs(value);
  const formatted = `${absValue.toFixed(decimals)}%`;
  return isNegative ? `(${formatted})` : formatted;
};

/**
 * Formats hours with a suffix: e.g. "32.50 hrs"
 */
export const formatHours = (hours: number | null | undefined, decimals: number = 2): string => {
  if (hours === null || hours === undefined || isNaN(hours)) {
    return `0.${'0'.repeat(decimals)} hrs`;
  }
  return `${hours.toFixed(decimals)} hrs`;
};

/**
 * Clean numeric format for machine-readable payroll exports (no commas, no $, negative with minus)
 */
export const formatPayrollNumber = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '0.00';
  }
  return amount.toFixed(2);
};
